import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { useAuth } from './contexts/AuthContext';
import LoadingScreen from './components/LoadingScreen';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import Dashboard from './pages/Dashboard';
import OrdersPage from './pages/OrdersPage';

import FulfillmentRulesPage from './pages/FulfillmentRulesPage';
import ImportRulesPage from './pages/ImportRulesPage';
import InventoryPage from './pages/InventoryPage';
import FulfillmentCentersPage from './pages/FulfillmentCentersPage';
import ProfilePage from './pages/ProfilePage';
import ApiLogsPage from './pages/ApiLogsPage';
import UsersPage from './pages/UsersPage';
import CODDashboard from './pages/CODDashboard';
import RecoveryPage from './pages/RecoveryPage';

// Protected Route component
const ProtectedRoute = ({ children, requireAdmin = false, requireUser = false }) => {
  const { isAuthenticated, isAdmin, isUser, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requireAdmin && !isAdmin()) {
    return <Navigate to="/dashboard" replace />;
  }

  if (requireUser && !isUser()) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route component (redirect to dashboard if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  const { loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          }
        />

        {/* Protected routes */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <Layout>
                <Routes>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/orders" element={<OrdersPage />} />

                  <Route 
                    path="/fulfillment-rules" 
                    element={
                      <ProtectedRoute requireUser>
                        <FulfillmentRulesPage />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/import-rules" 
                    element={
                      <ProtectedRoute requireUser>
                        <ImportRulesPage />
                      </ProtectedRoute>
                    } 
                  />
                  <Route path="/inventory" element={<InventoryPage />} />
                  <Route path="/cod-dashboard" element={<CODDashboard />} />
                  <Route 
                    path="/recovery" 
                    element={
                      <ProtectedRoute requireUser>
                        <RecoveryPage />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/fulfillment-centers" 
                    element={
                      <ProtectedRoute requireAdmin>
                        <FulfillmentCentersPage />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/api-logs" 
                    element={
                      <ProtectedRoute requireAdmin>
                        <ApiLogsPage />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/users" 
                    element={
                      <ProtectedRoute requireAdmin>
                        <UsersPage />
                      </ProtectedRoute>
                    } 
                  />
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </Layout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Box>
  );
}

export default App; 